import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useMemo,
} from "react";
import { AddOnsCatalogContext } from "@stores/addons/context";
import { AddOnProductType, IAddOnAxes } from "@stores/addons/types";
import { useConfig } from "@stores/config/hooks";
import { IPurchase } from "@stores/purchases/types";
import { buildCheckoutContext, buildPurchaseContext } from "./context";
import {
  billingRowItems,
  billsOnUsage,
  buildAddOnContext,
  DescriptorOf,
  getDescriptor,
  purchaseLineItems,
  visibleAddOns,
} from "./registry";
import {
  AddOnSlot,
  IAddOnContext,
  IAddOnRow,
  IPurchaseContext,
  IUpsertContext,
} from "./types";

const PurchaseContext = createContext<IPurchaseContext | null>(null);

export function useBuildPurchaseContext(purchase: IPurchase): IPurchaseContext;
export function useBuildPurchaseContext(
  purchase?: IPurchase,
): IPurchaseContext | undefined;
export function useBuildPurchaseContext(
  purchase?: IPurchase,
): IPurchaseContext | undefined {
  const config = useConfig();
  const addOns = useContext(AddOnsCatalogContext);

  return useMemo(
    () => purchase && buildPurchaseContext(purchase, config, addOns),
    [purchase, config, addOns],
  );
}

export function useCheckoutContext({
  isCloudHosted,
  isYearly,
}: IAddOnAxes): IUpsertContext {
  const config = useConfig();
  const addOns = useContext(AddOnsCatalogContext);

  return useMemo(
    () => buildCheckoutContext(config, addOns, { isCloudHosted, isYearly }),
    [config, addOns, isCloudHosted, isYearly],
  );
}

export function PurchaseContextProvider({
  purchase,
  children,
}: PropsWithChildren<{ purchase: IPurchase }>): ReactElement {
  const ctx = useBuildPurchaseContext(purchase);

  return (
    <PurchaseContext.Provider value={ctx}>{children}</PurchaseContext.Provider>
  );
}

export function usePurchaseContext(): IPurchaseContext {
  const ctx = useContext(PurchaseContext);

  if (!ctx) {
    throw new Error(
      "usePurchaseContext must be used within a PurchaseContextProvider",
    );
  }

  return ctx;
}

export function useAddOnContext<T extends AddOnProductType>(
  productType: T,
): IAddOnContext<DescriptorOf<T>> {
  const purchaseContext = usePurchaseContext();
  const descriptor = getDescriptor(productType);

  if (!descriptor) {
    throw new Error(
      `you must create a descriptor for product type ${productType}`,
    );
  }

  return useMemo(
    () => buildAddOnContext(descriptor as DescriptorOf<T>, purchaseContext),
    [descriptor, purchaseContext],
  );
}

export function useVisibleAddOnContext(
  productType: AddOnProductType,
): IAddOnContext | undefined {
  const addOnContext = useAddOnContext(productType);

  return addOnContext?.isVisible?.() ? addOnContext : undefined;
}

export function useAddOnsManageCards(): (IAddOnContext & {
  ManageCard: AddOnSlot;
})[] {
  const purchaseContext = usePurchaseContext();

  return useMemo(
    () =>
      visibleAddOns(purchaseContext).filter(
        (addOn): addOn is IAddOnContext & { ManageCard: AddOnSlot } =>
          !!addOn.ManageCard,
      ),
    [purchaseContext],
  );
}

export function useAddOnPurchaseLineItems(): IAddOnRow[] {
  const purchaseContext = usePurchaseContext();

  return useMemo(() => purchaseLineItems(purchaseContext), [purchaseContext]);
}

/** the billing page's add-on rows, none while the purchase is still loading */
export function useAddOnBillingRowItems(ctx?: IPurchaseContext): IAddOnRow[] {
  return useMemo(() => (ctx ? billingRowItems(ctx) : []), [ctx]);
}

export function useBillsOnUsage(): boolean {
  return billsOnUsage(usePurchaseContext());
}
